import React from "react";
import moment from "moment";
import logo from "../../../assets/images/asset-planet-background.png";
import warningSign from "../../../assets/SVGs/warningSign.svg";
import linkedIcon from "../../../assets/images/DeathModuleNew/accountLink.png";

export const LiabilityPDFReport = ({
  loanList,
  creditList,
  futureList,
  totalLoans,
  totalCredit,
  clientName,
  loanChartImage,
  creditChartImage,
  total,
  totalNoCredit,
  totalMonthly,
  totalCreditLimit,
  newLiabilitySum,
  liabilityCreditHigherValue,
  totalMonthDiff,
  loading,
}) => {
  const formatCurrency = (val) => {
    return (parseFloat(val) || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const calculateCreditUtilization = (creditRows, totalCreditBalance, totalCreditLimit) => {
    let balance = parseFloat(totalCreditBalance) || 0;
    let limit = parseFloat(totalCreditLimit) || 0;

    if (limit <= 0 && Array.isArray(creditRows) && creditRows.length > 0) {
      const totals = creditRows.reduce(
        (acc, row) => {
          acc.balance += parseFloat(row["Credit Balance"]) || 0;
          acc.limit += parseFloat(row["Credit Limit"]) || 0;
          return acc;
        },
        { balance: 0, limit: 0 }
      );
      if (totals.limit > 0) {
        balance = totals.balance;
        limit = totals.limit;
      }
    }
    if (limit <= 0) return "0.00%";
    const utilization = (balance / limit) * 100;
    return isNaN(utilization) || !isFinite(utilization) ? "0.00%" : `${utilization.toFixed(2)}%`;
  };

  const calculateAverageInterestRate = (creditRows) => {
    if (!Array.isArray(creditRows) || creditRows.length === 0) return "N/A";
    const totalRate = creditRows.reduce((sum, row) => {
      const rate = parseFloat(row["Interest Rate (or APR%)"]);
      return sum + (!isNaN(rate) ? rate : 0);
    }, 0);
    return (totalRate / creditRows.length).toFixed(2) + "%";
  };

  const formatMonths = (totalMonths) => {
    const total = parseFloat(totalMonths);
    if (isNaN(total) || total <= 0) return "N/A";
    const years = Math.floor(total / 12);
    const months = (total % 12).toFixed(1);
    let result = "";
    if (years > 0) result += `${years} year${years > 1 ? "s" : ""}`;
    if (parseFloat(months) > 0) result += ` ${months} month${parseFloat(months) > 1 ? "s" : ""}`;
    return result.trim() || "N/A";
  };

  const getLoanSumByModule = (list, moduleName) => {
    if (!list || !list.Liability || !Array.isArray(list.Liability)) return "$0.00";
    const sum = list.Liability.reduce((acc, curr) => {
      return curr.module_name && curr.module_name.toLowerCase() === moduleName.toLowerCase()
        ? acc + Number(curr.sum || 0)
        : acc;
    }, 0);
    return "$" + formatCurrency(sum);
  };

  const getOtherLoansSum = (list) => {
    if (!list || !list.Liability || !Array.isArray(list.Liability)) return "$0.00";
    const excluded = ["auto", "real estates", "student"];
    const total = list.Liability.reduce((acc, curr) => {
      const name = curr && curr.module_name && curr.module_name.toLowerCase();
      return name && !excluded.includes(name) ? acc + (curr.sum || 0) : acc;
    }, 0);
    return "$" + formatCurrency(total);
  };

  return (
    <div
      id="liability-report-pdf"
      style={{
        padding: "60px 30px",
        backgroundColor: "#fff",
        width: "950px",
        margin: "0 auto",
        fontFamily: "'Inter', sans-serif",
        color: "#000",
        boxSizing: "border-box",
      }}
    >
      {/* Logo and Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "30px",
          borderBottom: "2px solid #eee",
          paddingBottom: "20px",
        }}
      >
        <img src={logo} alt="Logo" style={{ height: "50px", objectFit: "contain" }} />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "14px", color: "#666", fontWeight: "600" }}>Report Generated</div>
          <div style={{ fontSize: "16px", fontWeight: "bold", color: "#1F3C88" }}>
            {moment().format("MMMM D, YYYY")}
          </div>
        </div>
      </div>

      <h1 style={{ fontSize: "32px", fontWeight: "800", marginBottom: "5px", color: "#1c1c1c", letterSpacing: "-1px" }}>
        Liabilities & Credit Report
      </h1>
      <div style={{ fontSize: "16px", color: "#666", fontWeight: "600", marginBottom: "25px" }}>
        Client: <span style={{ color: "#1F3C88", fontWeight: "800" }}>{clientName || "N/A"}</span>
      </div>

      {/* Summary Section - Grid of 4 Cards */}
      <div data-pdf-section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "40px" }}>
        {/* Card 1: Total Credit Lines */}
        <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "12px", border: "1px solid #eee" }}>
          <h3 style={{ fontSize: "14px", color: "#1F3C88", fontWeight: "bold", marginBottom: "12px", borderBottom: "1px solid #ddd", paddingBottom: "5px" }}>
            Total Credit Lines
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Number Credit Cards and Loans:</span>
              <span style={{ fontWeight: "600" }}>{(Number(total) || 0) + (Number(totalNoCredit) || 0)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Total Amount Cards and Loans:</span>
              <span style={{ fontWeight: "600" }}>${formatCurrency((parseFloat(totalLoans) || 0) + (parseFloat(totalCredit) || 0))}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Total Monthly Payments:</span>
              <span style={{ fontWeight: "600" }}>${formatCurrency(totalMonthly)}</span>
            </div>
          </div>
        </div>

        {/* Card 2: Loan Balances */}
        <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "12px", border: "1px solid #eee" }}>
          <h3 style={{ fontSize: "14px", color: "#1F3C88", fontWeight: "bold", marginBottom: "12px", borderBottom: "1px solid #ddd", paddingBottom: "5px" }}>
            Total Loan Balances
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Total Auto Loans:</span>
              <span style={{ fontWeight: "600" }}>{getLoanSumByModule(newLiabilitySum, "auto")}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Total Home Loans:</span>
              <span style={{ fontWeight: "600" }}>{getLoanSumByModule(newLiabilitySum, "real estates")}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Total Student Loans:</span>
              <span style={{ fontWeight: "600" }}>{getLoanSumByModule(newLiabilitySum, "student")}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Total Other Loans:</span>
              <span style={{ fontWeight: "600" }}>{getOtherLoansSum(newLiabilitySum)}</span>
            </div>
          </div>
        </div>

        {/* Card 3: Credit Card Details */}
        <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "12px", border: "1px solid #eee" }}>
          <h3 style={{ fontSize: "14px", color: "#1F3C88", fontWeight: "bold", marginBottom: "12px", borderBottom: "1px solid #ddd", paddingBottom: "5px" }}>
            Total Credit Card Balances
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Total Number of Cards:</span>
              <span style={{ fontWeight: "600" }}>{Number(totalNoCredit) || 0}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Total Balances:</span>
              <span style={{ fontWeight: "600" }}>${formatCurrency(totalCredit)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Total Credit Card Utilization:</span>
              <span style={{ fontWeight: "600" }}>{calculateCreditUtilization(creditList, totalCredit, totalCreditLimit)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Average Interest Rate:</span>
              <span style={{ fontWeight: "600" }}>{calculateAverageInterestRate(creditList)}</span>
            </div>
          </div>
        </div>

        {/* Card 4: Details */}
        <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "12px", border: "1px solid #eee" }}>
          <h3 style={{ fontSize: "14px", color: "#1F3C88", fontWeight: "bold", marginBottom: "12px", borderBottom: "1px solid #ddd", paddingBottom: "5px" }}>
            Loan and Credit Card Details
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Highest Loan:</span>
              <span style={{ fontWeight: "600" }}>
                {liabilityCreditHigherValue && liabilityCreditHigherValue.highestLoanBalance ? `$${formatCurrency(liabilityCreditHigherValue.highestLoanBalance)}` : "N/A"}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Highest Interest Rate:</span>
              <span style={{ fontWeight: "600" }}>{liabilityCreditHigherValue && liabilityCreditHigherValue.max_interest_rate ? `${liabilityCreditHigherValue.max_interest_rate}%` : "N/A"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Highest Monthly Payment:</span>
              <span style={{ fontWeight: "600" }}>
                {liabilityCreditHigherValue && liabilityCreditHigherValue.max_monthly_payment ? `$${formatCurrency(liabilityCreditHigherValue.max_monthly_payment)}` : "N/A"}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
              <span style={{ color: "#666" }}>Average Credit Age:</span>
              <span style={{ fontWeight: "600" }}>{formatMonths(totalMonthDiff)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div data-pdf-section style={{ display: "flex", gap: "20px", marginBottom: "60px" }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "15px", color: "#333", borderLeft: "5px solid #1F3C88", paddingLeft: "15px" }}>
            Loans Balance
          </h2>
          <div style={{ backgroundColor: "#fff", padding: "15px", borderRadius: "12px", border: "1px solid #f0f0f0", textAlign: "center", minHeight: "250px" }}>
            {loanChartImage ? (
              <img src={loanChartImage} alt="Loan Chart" style={{ maxWidth: "100%", height: "auto" }} />
            ) : (
              <div style={{ color: "#ccc", paddingTop: "100px", fontStyle: "italic" }}>No chart data</div>
            )}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "15px", color: "#333", borderLeft: "5px solid #2A3DA3", paddingLeft: "15px" }}>
            Credit Utilization
          </h2>
          <div style={{ backgroundColor: "#fff", padding: "15px", borderRadius: "12px", border: "1px solid #f0f0f0", textAlign: "center", minHeight: "250px" }}>
            {creditChartImage ? (
              <img src={creditChartImage} alt="Credit Chart" style={{ maxWidth: "100%", height: "auto" }} />
            ) : (
              <div style={{ color: "#ccc", paddingTop: "100px", fontStyle: "italic" }}>No chart data</div>
            )}
          </div>
        </div>
      </div>

      {/* Loans Table */}
      <h2 style={{ fontSize: "22px", fontWeight: "bold", marginBottom: "20px", color: "#1c1c1c", borderLeft: "5px solid #1F3C88", paddingLeft: "15px" }}>
        Loans (Not Credit Cards)
      </h2>
      <table data-pdf-section style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", marginBottom: "50px", borderRadius: "12px", overflow: "hidden", border: "1px solid #E0E0E0" }}>
        <thead>
          <tr style={{ backgroundColor: "#1F3C88", color: "#fff" }}>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Name of Liability</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Owner</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Institution</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Monthly Pmt</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>APR</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Maturity Date</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Current Balance</th>
            <th style={{ padding: "12px 14px", textAlign: "center", fontSize: "12px" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {loanList && loanList.length > 0 ? (
            loanList.map((loan, index) => {
              const isIncomplete = !loan["Start Date"] || !loan["Maturity Date"] || !loan["Interest Rate"] || !loan["Initial Loan Amount"];
              return (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#fcfcfc" }}>
                  <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{loan["Nickname of Liabilities"] || "N/A"}</td>
                  <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{loan["Owner"] || "N/A"}</td>
                  <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{loan["Name of Financial Institution"] || "N/A"}</td>
                  <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>${formatCurrency(loan["Monthly Payment"])}</td>
                  <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{parseFloat(loan["Interest Rate"] || 0).toFixed(2)}%</td>
                  <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{loan["Maturity Date"] ? moment(loan["Maturity Date"]).format("MM/DD/YYYY") : "N/A"}</td>
                  <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px", fontWeight: "bold" }}>${formatCurrency(loan["Loan Balance"] || loan["Initial Loan Amount"])}</td>
                  <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px", textAlign: "center" }}>
                    <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
                      {loan.managedBy === "Plaid" && <img src={linkedIcon} alt="Plaid" style={{ height: "14px" }} />}
                      {isIncomplete && <img src={warningSign} alt="Missing Data" style={{ height: "14px" }} />}
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr><td colSpan="8" style={{ padding: "30px", textAlign: "center", color: "#999" }}>No loans found</td></tr>
          )}
        </tbody>
      </table>

      {/* Credit Cards Table */}
      <h2 style={{ fontSize: "22px", fontWeight: "bold", marginBottom: "20px", color: "#1c1c1c", borderLeft: "5px solid #2A3DA3", paddingLeft: "15px" }}>
        Credit Line (Bank or Credit Card)
      </h2>
      <table data-pdf-section style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", marginBottom: "50px", borderRadius: "12px", overflow: "hidden", border: "1px solid #E0E0E0" }}>
        <thead>
          <tr style={{ backgroundColor: "#2A3DA3", color: "#fff" }}>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Name of Credit</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Owner</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Bank</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Account No.</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Credit Limit</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Monthly Pmt</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>APR</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Current Balance</th>
          </tr>
        </thead>
        <tbody>
          {creditList && creditList.length > 0 ? (
            creditList.map((credit, index) => (
              <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#fcfcfc" }}>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{credit["Nickname of Credit"] || "N/A"}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{credit["Owner"] || "N/A"}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{credit["Name of Issuer"] || "N/A"}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{credit["Account Number"] ? "****" + credit["Account Number"] : "N/A"}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>${formatCurrency(credit["Credit Limit"])}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>${formatCurrency(credit["Monthly Payment"])}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{parseFloat(credit["Interest Rate (or APR%)"] || 0).toFixed(2)}%</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px", fontWeight: "bold" }}>${formatCurrency(credit["Credit Balance"])}</td>
              </tr>
            ))
          ) : (
            <tr><td colSpan="8" style={{ padding: "30px", textAlign: "center", color: "#999" }}>No credit cards found</td></tr>
          )}
        </tbody>
      </table>

      {/* Future Liabilities Table */}
      <h2 style={{ fontSize: "22px", fontWeight: "bold", marginBottom: "20px", color: "#1c1c1c", borderLeft: "5px solid #FF9800", paddingLeft: "15px" }}>
        Future Liabilities
      </h2>
      <table data-pdf-section style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", marginBottom: "50px", borderRadius: "12px", overflow: "hidden", border: "1px solid #E0E0E0" }}>
        <thead>
          <tr style={{ backgroundColor: "#FF9800", color: "#fff" }}>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Asset Nickname</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Owner</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Bank Name</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Est. Loan Amount</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Start Date</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Est. APR</th>
            <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px" }}>Monthly Pmt</th>
          </tr>
        </thead>
        <tbody>
          {futureList && futureList.length > 0 ? (
            futureList.map((future, index) => (
              <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#fcfcfc" }}>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{future["Asset Nickname"] || "N/A"}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{future["Owner"] || "N/A"}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{future["Bank Name"] || "N/A"}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>${formatCurrency(future["Est. Loan Amount"])}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{future["Loan Start Date"] ? moment(future["Loan Start Date"]).format("MM/DD/YYYY") : "N/A"}</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px" }}>{parseFloat(future["Est. APR(%)"] || 0).toFixed(2)}%</td>
                <td style={{ padding: "12px 14px", borderTop: "1px solid #eee", fontSize: "12px", fontWeight: "bold" }}>${formatCurrency(future["Monthly Payment"])}</td>
              </tr>
            ))
          ) : (
            <tr><td colSpan="7" style={{ padding: "30px", textAlign: "center", color: "#999" }}>No future liabilities found</td></tr>
          )}
        </tbody>
      </table>

      {/* Footer */}
      <div
        data-pdf-section
        style={{
          fontSize: "11px",
          lineHeight: "1.6",
          color: "#666",
          marginTop: "40px",
          textAlign: "center",
          padding: "20px",
          borderTop: "1px solid #eee",
        }}
      >
        <strong>Confidential & Unaudited:</strong> This report is intended solely for the named recipient and is for informational purposes only. The data reflects information provided by the user and has not been audited by <strong>Asset Planet.</strong> Asset Planet makes no warranty regarding the data's accuracy or completeness and does not provide financial, legal, or tax advice through this report.
      </div>
    </div>
  );
};
